import { useEffect, useMemo } from 'react';
import { Dropdown, InlineLoading } from '@carbon/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { debounce } from 'lodash-es';
import { FreeTextComboBox } from 'components/free-text-combobox';
import * as sharedAtoms from './atoms';
import type {
  BaseAreaSelection,
  FetchGeometryAtom,
  ScenarioPickerForm,
} from './types';
import styles from './scenario-area-configs.module.scss';

/**
 * The atoms the area fields read and write. Each scenario form keeps its own copy of them, so the
 * form passes its own; the shared ones are the default.
 */
export type AreaTypeFieldsAtoms = Pick<
  typeof sharedAtoms,
  | 'categoriesLoadableAtom'
  | 'areaSearchLoadableAtom'
  | 'areaCategoryAtom'
  | 'areaQueryAtom'
  | 'areaGeometriesAtom'
  | 'toastAtom'
> & { fetchGeometryLoadableAtom: FetchGeometryAtom<'area'> };

type PickedItem = { value?: string; id?: number };

export interface AreaTypeFieldsProps {
  form: ScenarioPickerForm;
  areaIndex: number;
  atoms?: AreaTypeFieldsAtoms;
  /** The category dropdown's placeholder. */
  categoryLabel?: string;
  /** Whether each area option is drawn in the wrapping menu-item span. */
  wrapAreaOptions?: boolean;
}

/**
 * Area-type fields: category dropdown + area ComboBox with typeahead search, shared by every scenario
 * form that picks an area.
 * @param {AreaTypeFieldsProps} props - The form, the area index, the atoms to use and the wording
 * @return {JSX.Element} The area type fields
 */
export function AreaTypeFields({
  form,
  areaIndex,
  atoms = sharedAtoms,
  categoryLabel = 'Choose a category',
  wrapAreaOptions = true,
}: AreaTypeFieldsProps): JSX.Element {
  const prefix = `areas[${areaIndex}]` as const;
  const categoriesState = useAtomValue(atoms.categoriesLoadableAtom);
  const areaSearchState = useAtomValue(atoms.areaSearchLoadableAtom);
  const setAreaCategory = useSetAtom(atoms.areaCategoryAtom);
  const setAreaQuery = useSetAtom(atoms.areaQueryAtom);
  const fetchGeometry = useSetAtom(atoms.fetchGeometryLoadableAtom);
  const setGeometries = useSetAtom(atoms.areaGeometriesAtom);
  const setToast = useSetAtom(atoms.toastAtom);

  useEffect(() => {
    if (areaSearchState.state === 'hasError') {
      setToast({ message: 'Failed to search areas', type: 'error' });
    }
  }, [areaSearchState.state, setToast]);

  const categoryItems =
    categoriesState.state === 'hasData' && categoriesState.data
      ? categoriesState.data.categories
      : [];

  const debouncedAreaQuery = useMemo(
    () => debounce((text: string) => setAreaQuery(text), 350),
    [setAreaQuery]
  );

  useEffect(() => {
    return () => debouncedAreaQuery.cancel();
  }, [debouncedAreaQuery]);

  const areaItems = useMemo(() => {
    if (areaSearchState.state !== 'hasData' || !areaSearchState.data) return [];
    return areaSearchState.data.results.map((r) => {
      const label =
        [r.purpose, r.name].filter(Boolean).join(' - ') || r.displayName;
      return { id: r.id, value: label, label };
    });
  }, [areaSearchState]);

  /**
   * Drops the geometry shown for an entry whose area or unit was cleared.
   * @param {number} entryId - The form entry
   * @return {void}
   */
  const forgetGeometry = (entryId: number) =>
    setGeometries((prev) => {
      const next = { ...prev };
      delete next[entryId];
      return next;
    });

  return (
    <div className={styles.formGrid}>
      {categoriesState.state === 'loading' ? (
        <InlineLoading description="Loading categories..." />
      ) : (
        <form.Field name={`${prefix}.category`}>
          {(field) => (
            <Dropdown
              id={`area-category-${areaIndex}`}
              titleText="Area Category (required)"
              label={categoryLabel}
              size="md"
              light
              items={categoryItems}
              selectedItem={(field.state.value as string) || null}
              onChange={({ selectedItem }: { selectedItem: string }) => {
                const entryId = form.getFieldValue(`${prefix}.id`) as
                  number | undefined;
                field.handleChange(selectedItem || '');
                form.setFieldValue(`${prefix}.areaName`, '');
                form.setFieldValue(`${prefix}.configurableId`, null);
                setAreaCategory(selectedItem || '');
                setAreaQuery(undefined);
                if (entryId) forgetGeometry(entryId);
              }}
            />
          )}
        </form.Field>
      )}

      <form.Subscribe
        selector={(state) => {
          const areas = (state.values as { areas: BaseAreaSelection[] }).areas;
          return {
            entryId: areas[areaIndex]?.id,
            category: areas[areaIndex]?.category,
            allAreas: areas,
          };
        }}
      >
        {({ entryId, category, allAreas }) => {
          // Dedupe "already used" areas by the stable area id, not the display
          // name: two distinct areas can share a name, and the catalog lists one
          // current version per business area, so the id is the reliable identity.
          // (a.areaName keeps this scoped to area-type selections.)
          const usedAreaIds = allAreas
            .filter((_, i) => i !== areaIndex)
            .filter((a) => a.areaName)
            .map((a) => a.configurableId)
            .filter((id): id is number => id !== null);

          const filteredItems = areaItems.filter(
            (item) => !usedAreaIds.includes(item.id)
          );

          return (
            <div
              className={styles.typeaheadWrapper}
              onFocus={() => {
                if (
                  category &&
                  areaSearchState.state !== 'loading' &&
                  !areaItems.length
                ) {
                  setAreaCategory(category);
                  setAreaQuery('');
                }
              }}
            >
              <form.Field name={`${prefix}.areaName`}>
                {(field) => (
                  <FreeTextComboBox
                    id={`area-selection-${areaIndex}`}
                    titleText="Area (required)"
                    placeholder="Search areas..."
                    items={filteredItems}
                    selectedItem={
                      field.state.value
                        ? {
                            value: field.state.value as string,
                            label: field.state.value as string,
                          }
                        : null
                    }
                    itemToString={(item) =>
                      (item?.label as string | undefined) ?? ''
                    }
                    itemToElement={
                      wrapAreaOptions
                        ? (item) => (
                            <span className={styles.areaMenuItem}>
                              {item.label}
                            </span>
                          )
                        : undefined
                    }
                    onChange={({ selectedItem }) => {
                      const picked = selectedItem as PickedItem | null;
                      field.handleChange(picked?.value ?? '');
                      form.setFieldValue(
                        `${prefix}.configurableId`,
                        picked?.id ?? null
                      );
                      if (picked?.id && entryId) {
                        fetchGeometry({
                          entryId,
                          type: 'area',
                          configurableId: picked.id,
                        }).catch(() => {
                          field.handleChange('');
                          form.setFieldValue(`${prefix}.configurableId`, null);
                        });
                      } else if (entryId) {
                        forgetGeometry(entryId);
                      }
                    }}
                    onInputChange={(text) => debouncedAreaQuery(text)}
                  />
                )}
              </form.Field>
              {areaSearchState.state === 'loading' && (
                <InlineLoading description="" className={styles.spinner} />
              )}
            </div>
          );
        }}
      </form.Subscribe>
    </div>
  );
}
